/**
 * Zod schemas shared by the frontend (form validation) and backend (request validation).
 * The submission "data" blob mirrors the original prototype's draft object; most fields are
 * optional strings so partially-completed drafts round-trip cleanly.
 */
import { z } from 'zod';
// A single document entry in a submission.
export const docEntrySchema = z
    .object({
    id: z.string(),
    kind: z.string(),
    label: z.string().optional(),
    required: z.boolean().optional(),
    requested: z.boolean().optional(),
    attached: z.boolean().optional(),
    extra: z.string().optional().default(''),
    fileName: z.string().optional(),
    storagePath: z.string().optional(), // path in Supabase Storage (no inline base64)
    skipReason: z.string().optional(),
    reason: z.string().optional(),
})
    .passthrough();
// A carrier quote attached to a submission.
export const quoteSchema = z.object({
    carrier: z.string(),
    status: z.string().optional(),
    note: z.string().optional(),
    docName: z.string().optional(),
    storagePath: z.string().optional(),
    payment: z.string().optional(),
    lobs: z
        .array(z.object({ lob: z.string(), premium: z.union([z.string(), z.number()]).optional() }))
        .optional(),
});
export const statusHistoryEntrySchema = z.object({
    status: z.string(),
    at: z.number(),
    note: z.string().optional(),
});
// A message in the agent <-> specialist thread on a submission.
export const messageSchema = z.object({
    id: z.string(),
    from: z.enum(['agent', 'staff']),
    author: z.string().optional(),
    text: z.string().min(1).max(4000),
    at: z.number(),
});
// A STAFF-ONLY internal note on a submission. Never returned to agents (the public
// retrieve endpoint strips it) — for AAS staff to coordinate (e.g. "already emailed
// the BITCO UW on 8/25").
export const internalNoteSchema = z.object({
    id: z.string(),
    author: z.string().optional(),
    text: z.string().min(1).max(4000),
    at: z.number(),
});
// The full submission payload (stored as jsonb `data`). Kept permissive on purpose.
export const submissionDataSchema = z
    .object({
    id: z.string().optional(),
    tier: z.string().optional().default(''),
    status: z.string().optional(),
    prevStatus: z.string().optional(),
    unread: z.boolean().optional(), // shared "needs a look" flag for the staff queue glow
    // agency / submitter
    agencyName: z.string().optional().default(''),
    agencyMemberId: z.string().optional().default(''),
    agencyAccountId: z.string().optional().default(''),
    producer: z.string().optional().default(''),
    submitterPhone: z.string().optional().default(''),
    submitterEmail: z.string().optional().default(''),
    // insured
    businessName: z.string().optional().default(''),
    inAms: z.string().optional().default(''),
    amsNumber: z.string().optional().default(''),
    primaryContact: z.string().optional().default(''),
    phone: z.string().optional().default(''),
    email: z.string().optional().default(''),
    physicalAddress: z.string().optional().default(''),
    mailingAddress: z.string().optional().default(''),
    website: z.string().optional().default(''),
    fein: z.string().optional().default(''),
    yearsInBusiness: z.string().optional().default(''),
    // coverage
    lines: z.array(z.string()).optional().default([]),
    incumbentCarrier: z.string().optional().default(''),
    policyExpiration: z.string().optional().default(''),
    quoteNeedBy: z.string().optional().default(''),
    targetPricing: z.string().optional().default(''),
    accountType: z.string().optional().default(''), // 'new_agency' | 'owned'
    quoteTiming: z.string().optional().default(''), // 'renewal' | 'midterm'
    isRenewal: z.string().optional().default(''), // derived from quoteTiming; kept for downstream mapping
    isMidTerm: z.string().optional().default(''), // derived from quoteTiming; kept for downstream mapping
    midTermReason: z.string().optional().default(''),
    // detail
    descriptionOfOps: z.string().optional().default(''),
    grossAnnualRevenue: z.string().optional().default(''),
    annualPayroll: z.string().optional().default(''),
    knownLosses: z.string().optional().default(''),
    sicNaics: z.string().optional().default(''),
    referredBy: z.string().optional().default(''),
    additionalDetails: z.string().optional().default(''),
    executiveSummary: z.string().optional().default(''),
    // pipeline
    documents: z.array(docEntrySchema).optional().default([]),
    quotes: z.array(quoteSchema).optional().default([]),
    statusHistory: z.array(statusHistoryEntrySchema).optional().default([]),
    // Audit trail for manual tier reassignments (e.g. staff dragging a card to another
    // lane on the queue board). `from` is omitted when the submission had no tier yet.
    tierHistory: z
        .array(z.object({ from: z.string().optional(), to: z.string(), at: z.number() }))
        .optional(),
    // When staff drag a card to the board's Completed lane. Present = completed; once it's
    // older than the archive window the board treats it as Archived (hidden by default).
    completedAt: z.number().optional(),
    // Why a submission was completed when it did NOT bind (required at completion time
    // unless the status is already 'bound'). Staff-only — stripped from agent retrieve,
    // since some reasons (e.g. "not a genuine quote request") aren't for the agent.
    completedReason: z.string().optional(),
    messages: z.array(messageSchema).optional().default([]),
    // Staff-only; stripped from the agent-facing retrieve response in public.ts.
    internalNotes: z.array(internalNoteSchema).optional(),
    // Which AAS staffer owns this submission (shown on the queue board). Staff-only —
    // also stripped from the agent-facing retrieve.
    assignedTo: z.string().optional(),
    // Automated-nudge bookkeeping (system): when the current unanswered ask started,
    // how many follow-ups sent, when the last one went, and — if the agent never
    // responded — when we sent the final closing notice and auto-closed it. The
    // closedAt marker keeps a staff-reopened submission from being re-closed for the
    // same unanswered ask. Stripped from agent retrieve.
    nudge: z
        .object({
        sinceAt: z.number(),
        count: z.number(),
        lastAt: z.number(),
        closedAt: z.number().optional(),
    })
        .optional(),
    // Gatekeeper carriers staff have selected for this submission, each with its own
    // progress (pending | submitted | quoted | declined). See GATEKEEPER_CARRIERS.
    gatekeeperCarriers: z
        .array(z.object({ key: z.string(), status: z.string(), at: z.number().optional() }))
        .optional(),
    // Per-carrier appetite acknowledgment the agent ticks on the Gatekeeper carrier step
    // ({ bitco: true, ... }) — proof they reviewed the appetite guide before submitting.
    appetiteAck: z.record(z.string(), z.boolean()).optional(),
    billPlanOptions: z.array(z.string()).optional(),
    proposalPremium: z.string().optional(),
    // Canonical bound figures staff enter when a deal binds — feed the COMM-PASS
    // commission book (auto-pull tracker on /solutions).
    boundPremium: z.string().optional(),
    commissionPct: z.string().optional(),
    effectiveDate: z.string().optional(),
    chosenBillPlan: z.string().optional(),
    downPayment: z.string().optional(),
    proposalNote: z.string().optional(),
    paymentNote: z.string().optional(),
    mgaName: z.string().optional(),
    proposalDoc: z.object({ path: z.string(), name: z.string(), at: z.number() }).optional(),
    docRequests: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        note: z.string().optional(),
        at: z.number(),
        storagePath: z.string().optional(),
        docName: z.string().optional(),
        fulfilledAt: z.number().optional(),
    }))
        .optional(),
    extracted: z.record(z.unknown()).optional(),
    linkedMember: z
        .object({
        memberId: z.string().optional(),
        name: z.string().optional(),
        accountId: z.string().optional(),
        source: z.string().optional(),
        confirmed: z.boolean().optional(),
    })
        .nullable()
        .optional(),
    createdAt: z.number().optional(),
})
    .passthrough(); // tolerate extra keys during migration
// ---- Public endpoint inputs (backend) ----
// Create/save a submission from the public agent side.
export const createSubmissionSchema = z.object({
    data: submissionDataSchema,
    turnstileToken: z.string().optional(), // required in production when Turnstile is enabled
});
// Retrieve a saved submission by its code + PIN.
export const retrieveSubmissionSchema = z.object({
    code: z.string().min(4).max(24),
    pin: z.string().min(4).max(12),
});
// ---- Messaging ----
export const staffMessageSchema = z.object({
    submissionId: z.string(),
    text: z.string().min(1).max(4000),
});
export const publicMessageSchema = z.object({
    code: z.string().min(4).max(24),
    pin: z.string().min(4).max(12),
    text: z.string().min(1).max(4000),
});
// ---- AI extraction (backend proxy to Anthropic) ----
export const extractFileSchema = z.object({
    mime: z.string(),
    data: z.string(), // base64
    name: z.string().optional(),
});
export const extractRequestSchema = z.object({
    submissionId: z.string().optional(),
    files: z.array(extractFileSchema).min(1).max(10),
});
// ---- Salesforce sync (backend proxy to Power Automate) ----
export const salesforceSyncSchema = z.object({
    kind: z.enum(['submission', 'solution']),
    id: z.string(),
});
// ---- Reference data (staff-editable) ----
export const memberSchema = z.object({
    id: z.string(),
    name: z.string(),
    accountId: z.string().optional(),
});
